/**
 * Icon With Label Component
 * Reusable component for displaying an icon with a label/name
 * Used across Activities & Attractions, Amenities & Facilities, and Safety & Security sections
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import { IconWithBackground } from './IconWithBackground';
import { useUnifiedIcon } from '@/hooks/useUnifiedIcon';

export interface IconWithLabelProps {
  /** The label/text to display */
  label: string;
  /** Optional icon name/path from API */
  icon?: string | null;
  /** Optional ID for icon mapping (used for amenities) */
  id?: string;
  /** Size of the icon in pixels */
  iconSize?: number;
  /** Type of icon lookup (activity, amenity, etc.) */
  type?: 'activity' | 'amenity' | 'auto';
  /** Text color - defaults to primary */
  textColor?: string;
  /** Line height - defaults to normal */
  lineHeight?: 'normal' | 'relaxed';
  /** Custom color function for special cases (e.g., Emergency Services) */
  getCustomTextColor?: (label: string) => string | undefined;
  /** Custom color function for icon wrapper */
  getCustomIconColor?: (label: string) => string | undefined;
  /** Show icon even if not found - defaults to true */
  showIcon?: boolean;
}

/**
 * Icon With Label Component
 * Displays an icon with a label, handling all variations used across travel guide sections
 */
export const IconWithLabel: React.FC<IconWithLabelProps> = ({
  label,
  icon = null,
  id,
  iconSize = 20,
  type = 'auto',
  textColor,
  lineHeight = 'normal',
  getCustomTextColor,
  getCustomIconColor,
  showIcon = true,
}) => {
  const { icon: resolvedIcon } = useUnifiedIcon({
    label,
    icon,
    id,
    iconSize,
    type,
  });

  // Apply custom color function if provided
  const finalTextColor =
    textColor || getCustomTextColor?.(label) || typographyColors.primary;
  const finalIconColor = getCustomIconColor?.(label);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing[2],
      }}
    >
      {showIcon && resolvedIcon && (
        <div
          style={{
            flexShrink: 0,
            color: finalIconColor || 'inherit',
          }}
        >
          <IconWithBackground
            icon={resolvedIcon}
            iconSize={iconSize}
            alt={label}
          />
        </div>
      )}
      <span
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.sm,
          fontWeight: typography.weight.regular,
          color: finalTextColor,
          textAlign: 'left',
          lineHeight:
            lineHeight === 'relaxed'
              ? typography.lineHeight.relaxed
              : typography.lineHeight.normal,
          flex: 1,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          minWidth: 0,
          margin: 0,
        }}
      >
        {label}
      </span>
    </div>
  );
};
